import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'http://192.168.1.33:5000/api';

const servicePorts = {
  wireguard: '10086/#/signin',
  pihole: 'admin/login',
  bazarr: '6767/',
  overseerr: '5055/',
  nginxproxymanager: '81/login',
  radarr: '7878/',
  plex: '32400/',
  qbittorrent: '8090/',
  jackett: '9117/UI/Dashboard',
  sonarr: '8989/',
  cockpit: '9090/',
  gotify: '#/login',
  dockge: '5001/',
  vaultwarden: '',
  frigate: '5000/',
  proxmox: '8006/'
};

const serviceLabels = {
  wireguard: 'WG',
  pihole: 'PI',
  bazarr: 'BZ',
  overseerr: 'OS',
  nginxproxymanager: 'NX',
  radarr: 'RD',
  plex: 'PX',
  qbittorrent: 'QB',
  jackett: 'JK',
  sonarr: 'SN',
  cockpit: 'CP',
  gotify: 'GF',
  dockge: 'DG',
  vaultwarden: 'VW',
  frigate: 'FR',
  ubuntu: 'DB',
  haos: 'HA',
  windows: 'W11'
};

const pct = (value) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
};

const uptime = (seconds) => {
  if (!seconds) return '0m';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days ? `${days}d ${hours}h` : `${hours}h ${minutes}m`;
};

const severityFromPercent = (value) => {
  if (value === null || value === undefined) return 'unknown';
  if (value >= 90) return 'critical';
  if (value >= 75) return 'warning';
  return 'ok';
};

const severityFromTemp = (value, kind = 'cpu') => {
  const n = parseFloat(String(value || '').replace(',', '.'));
  if (!Number.isFinite(n)) return 'unknown';
  if (kind === 'disk') return n >= 60 ? 'critical' : n >= 45 ? 'warning' : 'ok';
  return n >= 85 ? 'critical' : n >= 70 ? 'warning' : 'ok';
};

const normalized = (name = '') => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const guessService = (vm) => {
  const key = normalized(vm.name || vm.originalName);
  if (key.includes('nginxproxy')) return 'nginxproxymanager';
  if (key.includes('qbit')) return 'qbittorrent';
  if (key.includes('windows')) return 'windows';
  if (key.includes('frigate')) return 'frigate';
  return Object.keys(serviceLabels).find((candidate) => key.includes(candidate)) || vm.serviceType || vm.type || 'vm';
};

const linkFor = (vm) => {
  if (vm.ipAddress && vm.ipAddress !== 'N/A') {
    const host = vm.ipAddress.split('/')[0];
    const service = guessService(vm);
    const portPath = servicePorts[service];
    if (portPath === undefined) return vm.url || `http://${host}`;
    if (portPath === '') return `http://${host}`;
    const protocol = service === 'cockpit' || service === 'proxmox' ? 'https' : 'http';
    return `${protocol}://${host}:${portPath}`;
  }
  return vm.url || null;
};

const useTheme = () => {
  const [dark, setDark] = useState(localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    document.body.classList.toggle('dark-theme', dark);
    localStorage.setItem('darkMode', dark);
  }, [dark]);
  return [dark, setDark];
};

const Metric = ({ label, value, detail, tone }) => (
  <div className={`metric-tile ${tone || severityFromPercent(value)}`}>
    <div className="metric-top">
      <span>{label}</span>
      <strong>{value === null || value === undefined ? 'N/A' : `${pct(value)}%`}</strong>
    </div>
    <div className="meter">
      <span style={{ width: `${pct(value)}%` }} />
    </div>
    {detail && <small>{detail}</small>}
  </div>
);

const StatusPill = ({ value, label }) => (
  <span className={`pill ${value || 'unknown'}`}>{label || value || 'unknown'}</span>
);

const Login = ({ setAuth }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [dark, setDark] = useTheme();

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${API_URL}/login`, { username, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuth(true);
    } catch (err) {
      setError('Inloggning misslyckades.');
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-heading">
          <div>
            <span className="eyebrow">PVE</span>
            <h1>Proxmox Dashboard</h1>
          </div>
          <button className="icon-button" onClick={() => setDark(!dark)} type="button">
            {dark ? 'Ljus' : 'Mork'}
          </button>
        </div>
        <form onSubmit={handleLogin}>
          {error && <div className="error">{error}</div>}
          <label>
            Anvandarnamn
            <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" required />
          </label>
          <label>
            Losenord
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
          </label>
          <button className="primary-button" type="submit">Logga in</button>
        </form>
      </section>
    </main>
  );
};

const Dashboard = ({ setAuth }) => {
  const [dark, setDark] = useTheme();
  const [vms, setVms] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
  }, [setAuth]);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [vmResponse, nodeResponse, healthResponse] = await Promise.all([
        axios.get(`${API_URL}/proxmox/vms`, { headers }),
        axios.get(`${API_URL}/proxmox/nodes`, { headers }),
        axios.get(`${API_URL}/proxmox/health`, { headers })
      ]);
      setVms(vmResponse.data || []);
      setNodes(nodeResponse.data || []);
      setHealth(healthResponse.data || {});
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      if (err.response && [401, 403].includes(err.response.status)) {
        logout();
        return;
      }
      setError('Kunde inte hamta all dashboarddata.');
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const summary = useMemo(() => {
    const running = vms.filter((vm) => vm.status === 'running').length;
    const stopped = vms.filter((vm) => vm.status !== 'running').length;
    const node = nodes[0] || {};
    const zfsCritical = (health?.zfs || []).filter((pool) => pool.state !== 'ONLINE').length;
    const hotSensors = (health?.sensors || []).filter((sensor) => sensor.severity !== 'ok').length;
    const failedServices = health?.failedServices?.length || 0;
    const taskErrors = health?.taskErrors?.length || 0;
    return { running, stopped, node, zfsCritical, hotSensors, failedServices, taskErrors };
  }, [vms, nodes, health]);

  const sortedVms = useMemo(() => [...vms].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'running' ? -1 : 1;
    return Number(a.id) - Number(b.id);
  }), [vms]);

  const disks = nodes.flatMap((node) => node.disks || []);
  const physicalDisks = disks.filter((disk) => disk.type !== 'ZFS Storage Pool');
  const pools = health?.zfs?.length ? health.zfs : disks.filter((disk) => disk.type === 'ZFS Storage Pool');
  const sensors = health?.sensors || [];
  const cpuSensors = sensors.filter((sensor) => /tctl|tccd|package|cpu/i.test(`${sensor.chip} ${sensor.label}`));
  const diskSensors = sensors.filter((sensor) => /nvme|sata|ata/i.test(sensor.chip));
  const prioritizedSensors = [...cpuSensors, ...diskSensors, ...sensors.filter((sensor) => !cpuSensors.includes(sensor) && !diskSensors.includes(sensor))];

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <span className="eyebrow">pve - 192.168.1.87</span>
          <h1>Serverdrift</h1>
        </div>
        <div className="top-actions">
          <span className="updated">{lastUpdated ? lastUpdated.toLocaleTimeString('sv-SE') : 'Ingen data'}</span>
          <button className="icon-button" onClick={load} type="button">Uppdatera</button>
          <button className="icon-button" onClick={() => setDark(!dark)} type="button">{dark ? 'Ljus' : 'Mork'}</button>
          <button className="danger-button" onClick={logout} type="button">Logga ut</button>
        </div>
      </header>

      {error && <div className="error banner">{error}</div>}
      {loading ? <div className="loading">Laddar dashboarddata...</div> : (
        <>
          <section className="summary-grid">
            <Metric label="CPU" value={summary.node.cpu || 0} detail={summary.node.cpuModel} />
            <Metric label="Minne" value={summary.node.memory || 0} detail={`${summary.node.memoryTotal || 0} GB totalt`} />
            <Metric label="Lagring" value={summary.node.storage || 0} detail={`${summary.node.storageTotalGB || 0} GB pooler/rootfs`} />
            <Metric label="GPU" value={summary.node.gpuUsage} detail={health?.gpu?.[0]?.name || 'PCI detekterad'} tone={summary.node.gpuUsage == null ? 'unknown' : undefined} />
          </section>

          <section className="signal-strip">
            <div><strong>{summary.running}</strong><span>igang</span></div>
            <div><strong>{summary.stopped}</strong><span>stoppade</span></div>
            <div className={summary.zfsCritical ? 'critical' : 'ok'}><strong>{summary.zfsCritical}</strong><span>ZFS-larm</span></div>
            <div className={summary.hotSensors ? 'warning' : 'ok'}><strong>{summary.hotSensors}</strong><span>tempvarningar</span></div>
            <div className={summary.failedServices ? 'critical' : 'ok'}><strong>{summary.failedServices}</strong><span>failed services</span></div>
            <div className={summary.taskErrors ? 'warning' : 'ok'}><strong>{summary.taskErrors}</strong><span>task errors</span></div>
          </section>

          <section className="layout">
            <div className="main-column">
              <section className="section-block">
                <div className="section-title">
                  <h2>VM och LXC</h2>
                  <span>{vms.length} resurser</span>
                </div>
                <div className="resource-grid">
                  {sortedVms.map((vm) => {
                    const service = guessService(vm);
                    const href = linkFor(vm);
                    const cpu = pct(vm.cpu || 0);
                    const mem = pct(vm.memory || 0);
                    const disk = pct(vm.storage || 0);
                    const card = (
                      <article className={`resource-card ${vm.status}`}>
                        <div className="resource-head">
                          <span className="service-mark">{serviceLabels[service] || (vm.type === 'qemu' ? 'VM' : 'LX')}</span>
                          <div>
                            <h3>{vm.name || vm.originalName}</h3>
                            <small>VMID {vm.id} - {vm.type || vm.serviceType || 'resurs'} - {vm.ipAddress || 'N/A'}</small>
                          </div>
                          <StatusPill value={vm.status} label={vm.status === 'running' ? 'Igang' : 'Stoppad'} />
                        </div>
                        <div className="mini-metrics">
                          <span>CPU {cpu}%</span>
                          <span>RAM {mem}%</span>
                          <span>Disk {disk}%</span>
                        </div>
                      </article>
                    );
                    return href ? (
                      <a className="resource-link" href={href} target="_blank" rel="noreferrer" key={vm.id}>{card}</a>
                    ) : <div key={vm.id}>{card}</div>;
                  })}
                </div>
              </section>
            </div>

            <aside className="side-column">
              <section className="section-block">
                <div className="section-title">
                  <h2>Hardvara</h2>
                  <span>{uptime(summary.node.uptime)} uptime</span>
                </div>
                <div className="hardware-list">
                  {(health?.gpu || []).map((gpu, index) => (
                    <div className="hardware-row" key={`gpu-${index}`}>
                      <div><strong>{gpu.name}</strong><small>GPU</small></div>
                      <span>{gpu.usage ?? 'N/A'}% - {gpu.temperature ?? 'N/A'}C</span>
                    </div>
                  ))}
                  {(health?.pci || []).slice(0, 8).map((line, index) => (
                    <div className="hardware-row subtle" key={`pci-${index}`}>
                      <div><strong>{line.split(': ').pop()}</strong><small>{line.split(' ')[0]}</small></div>
                    </div>
                  ))}
                  {(health?.fans || []).map((fan, index) => (
                    <div className="hardware-row" key={`fan-${index}`}>
                      <div><strong>{fan.label}</strong><small>{fan.chip}</small></div>
                      <span>{fan.rpm} RPM</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="section-block">
                <div className="section-title">
                  <h2>Temperaturer</h2>
                  <span>{sensors.length} sensorer</span>
                </div>
                <div className="temp-grid">
                  {prioritizedSensors.slice(0, 14).map((sensor, index) => (
                    <div className={`temp-card ${severityFromTemp(sensor.value, /nvme|disk/i.test(sensor.chip) ? 'disk' : 'cpu')}`} key={`${sensor.chip}-${sensor.label}-${index}`}>
                      <strong>{Number(sensor.value).toFixed(1)}C</strong>
                      <span>{sensor.label}</span>
                      <small>{sensor.chip}</small>
                    </div>
                  ))}
                </div>
              </section>

              <section className="section-block">
                <div className="section-title">
                  <h2>ZFS och diskar</h2>
                  <span>{physicalDisks.length} diskar</span>
                </div>
                <div className="pool-list">
                  {pools.map((pool, index) => (
                    <div className={`pool-row ${pool.severity || (pool.health === 'ONLINE' ? 'ok' : 'critical')}`} key={`${pool.name}-${index}`}>
                      <div>
                        <strong>{pool.name}</strong>
                        <small>{pool.size} - {pool.allocated || pool.usage || ''}</small>
                      </div>
                      <StatusPill value={pool.state === 'ONLINE' || pool.health === 'ONLINE' ? 'running' : 'stopped'} label={pool.state || pool.health} />
                    </div>
                  ))}
                </div>
                <div className="disk-list">
                  {physicalDisks.map((disk) => (
                    <div className="disk-row" key={disk.name}>
                      <div>
                        <strong>{disk.name}</strong>
                        <small>{disk.model} - {disk.size} - wear {disk.wear || 'N/A'}</small>
                      </div>
                      <span className={severityFromTemp(disk.temperature, 'disk')}>{disk.temperature || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="section-block">
                <div className="section-title">
                  <h2>Fel och varningar</h2>
                  <span>{(health?.journal || []).length} rader</span>
                </div>
                <div className="event-list">
                  {(health?.failedServices || []).map((line, index) => (
                    <div className="event critical" key={`svc-${index}`}>{line}</div>
                  ))}
                  {(health?.taskErrors || []).slice(0, 6).map((task) => (
                    <div className="event warning" key={task.upid}>{task.type} {task.id || ''}: {task.status}</div>
                  ))}
                  {(health?.journal || []).slice(0, 10).map((line, index) => (
                    <div className="event" key={`journal-${index}`}>{line}</div>
                  ))}
                </div>
              </section>
            </aside>
          </section>
        </>
      )}
    </main>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('token')));

  return (
    <Router>
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <Login setAuth={setIsAuthenticated} /> : <Navigate to="/" />} />
        <Route path="/" element={isAuthenticated ? <Dashboard setAuth={setIsAuthenticated} /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
};

export default App;
